import { motion } from "framer-motion";
import {
  Sparkles,
  MessagesSquare,
  ListChecks,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    Icon: Sparkles,
    title: "Set a goal.",
    body:
      "Drop in a single goal: 'Launch MVP in 30 days', 'Raise a $2M seed', 'Hit $50k MRR'.",
    mock: "goal",
  },
  {
    n: "02",
    Icon: MessagesSquare,
    title: "Boardroom debates.",
    body:
      "Your eight agents argue strategy, surface risk, and converge on a decision with confidence scoring.",
    mock: "debate",
  },
  {
    n: "03",
    Icon: ListChecks,
    title: "Tasks land in your queue.",
    body:
      "Every decision becomes a tracked task — agent-owned where possible, HUMAN-flagged with a fix path when not.",
    mock: "tasks",
  },
];

function StepMock({ kind }) {
  if (kind === "goal") {
    return (
      <div className="card-soft flex h-full flex-col justify-center rounded-[24px] bg-gradient-to-br from-violet-50 to-white p-5">
        <div className="eyebrow text-violet-700">Goal</div>
        <div className="mt-2 text-[15px] font-bold text-zinc-900">
          Launch MVP in 30 days
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full bg-black px-3 py-1.5 text-[11px] font-semibold text-white">
          Run debate
          <ArrowRight className="h-3 w-3" strokeWidth={2.6} />
        </div>
      </div>
    );
  }
  if (kind === "debate") {
    return (
      <div className="card-soft flex h-full flex-col gap-2 rounded-[24px] bg-gradient-to-br from-cyan-50 to-white p-5">
        <div className="eyebrow text-cyan-700">Debate</div>
        {[
          { letter: "A", text: "Ship onboarding v1 in 2 sprints..." },
          { letter: "L", text: "Burn projection holds. Contractor OK." },
          { letter: "N", text: "Need SOC2 Type I now if enterprise." },
        ].map((m, i) => (
          <div
            key={i}
            className="flex items-start gap-2 rounded-xl border border-black/[0.04] bg-white px-2.5 py-2"
          >
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-extrabold text-white">
              {m.letter}
            </div>
            <div className="truncate text-[11px] text-zinc-700">{m.text}</div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="card-soft flex h-full flex-col gap-2 rounded-[24px] bg-gradient-to-br from-orange-50 to-white p-5">
      <div className="eyebrow text-orange-700">Tasks</div>
      {[
        { tag: "HUMAN", title: "File Delaware C-Corp" },
        { tag: "Atlas", title: "Refactor ingestion to serverless" },
        { tag: "Aura", title: "Waitlist re-activation drip" },
      ].map((t, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-xl border border-black/[0.04] bg-white px-2.5 py-2"
        >
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ${
              t.tag === "HUMAN"
                ? "bg-orange-100 text-orange-700"
                : "bg-zinc-100 text-zinc-700"
            }`}
          >
            {t.tag}
          </span>
          <span className="truncate text-[11px] font-semibold text-zinc-800">
            {t.title}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HowItWorks() {
  return (
    <section
      data-testid="how-it-works"
      id="how-it-works"
      className="px-5 py-24 md:px-8 md:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mb-14 max-w-2xl"
        >
          <div className="eyebrow">How it works</div>
          <h2 className="mt-3 text-[40px] font-black leading-[1.02] tracking-tight text-zinc-900 md:text-[60px]">
            From idea to IPO<br />in three moves.
          </h2>
        </motion.div>

        <div className="relative space-y-12">
          {/* Connecting gradient line */}
          <div
            aria-hidden
            className="absolute left-7 top-3 hidden h-[calc(100%-3rem)] w-[2px] bg-gradient-to-b from-violet-400 via-cyan-400 to-orange-300 md:block"
          />

          {STEPS.map(({ n, Icon, title, body, mock }, i) => (
            <motion.div
              key={n}
              data-testid={`how-step-${i + 1}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative grid grid-cols-1 items-center gap-6 md:grid-cols-2 md:gap-12 md:pl-20"
            >
              <div className="absolute left-0 top-1 hidden h-12 w-12 items-center justify-center rounded-2xl bg-white text-zinc-700 shadow-md md:flex">
                <Icon className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                  Step {n}
                </div>
                <h3 className="mt-1.5 text-[28px] font-extrabold tracking-tight text-zinc-900 md:text-[36px]">
                  {title}
                </h3>
                <p className="mt-3 max-w-md text-[14px] leading-relaxed text-zinc-600 md:text-[15px]">
                  {body}
                </p>
              </div>
              <div className="md:min-h-[180px]">
                <StepMock kind={mock} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
